import type { Config } from "dompurify";
import DOMPurify from "dompurify";
import { forwardRef, useMemo } from "react";
import "./email.css";

const defaultSanitizeConfig: Config = {
  ALLOWED_TAGS: [
    "p",
    "div",
    "span",
    "br",
    "strong",
    "b",
    "em",
    "i",
    "u",
    "a",
    "ul",
    "ol",
    "li",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "blockquote",
    "pre",
    "code",
    "table",
    "thead",
    "tbody",
    "tr",
    "td",
    "th",
    "img",
    "hr",
  ],
  ALLOWED_ATTR: [
    "href",
    "target",
    "src",
    "alt",
    "title",
    "class",
    "id",
    "style",
    "width",
    "height",
    "colspan",
    "rowspan",
  ],
  ALLOWED_URI_REGEXP:
    /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
  ADD_TAGS: ["style"],
  ADD_ATTR: ["target"],
  FORBID_TAGS: ["script", "object", "embed", "base", "link"],
  FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover"],
};

type Email = {
  subject: string | null;
  date: string | null;
  text?: string | null;
  html?: string | null;
};

type SanitizedHtmlResult = {
  html: string | null;
  error: Error | null;
};

const useSanitizedHtml = (
  rawHtml: string | null | undefined,
  config?: Config,
): SanitizedHtmlResult => {
  return useMemo(() => {
    if (!rawHtml) {
      return { html: null, error: null };
    }

    try {
      const sanitized = DOMPurify.sanitize(
        rawHtml,
        config || defaultSanitizeConfig,
      );
      return { html: sanitized, error: null };
    } catch (error) {
      console.warn("Failed to sanitize HTML content:", error);
      return { html: null, error: error as Error };
    }
  }, [rawHtml, config]);
};

type EmailViewerProps = {
  email: Email;
  showMetadata?: boolean;
  className?: string;
  sanitizeConfig?: Config;
  from?: string;
  to?: string[];
  cc?: string[];
  bcc?: string[];
  isForwarded?: boolean;
  forwardedFrom?: string;
};

export const EmailViewer = forwardRef<HTMLDivElement, EmailViewerProps>(
  (
    {
      email,
      showMetadata,
      className = "",
      sanitizeConfig,
      from,
      to,
      cc,
      bcc,
      isForwarded = false,
      forwardedFrom,
    },
    ref
  ) => {
    const { html: sanitizedHtml, error: sanitizeError } = useSanitizedHtml(
      email?.html,
      sanitizeConfig,
    );

  const renderContent = () => {
    // If HTML content exists and was successfully sanitized
    if (sanitizedHtml) {
      return (
        <div
          className="email-html-content prose max-w-none text-sm"
          style={{
            // Reset some common email styles
            fontFamily: "inherit",
            lineHeight: "1.6",
            color: "inherit",
          }}
          dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
        />
      );
    }

    // If HTML sanitization failed, show error with fallback
    if (email?.html && sanitizeError) {
      return (
        <div className="text-sm text-red-600 bg-red-50 p-3 rounded border">
          <div className="font-semibold mb-1">
            Unable to display HTML content
          </div>
          <div className="text-xs">Falling back to text content...</div>
          {email?.text && (
            <div className="mt-2 text-gray-700 whitespace-pre-wrap">
              {email.text}
            </div>
          )}
        </div>
      );
    }

    // Fall back to plain text if no HTML
    if (email?.text) {
      return (
        <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
          {email.text}
        </div>
      );
    }

    // No content available
    return (
      <div className="text-sm text-gray-500 italic">No content available</div>
    );
  };

  // Early return if no email data
  if (!email) {
    return (
      <div className="h-full overflow-auto bg-white flex items-center justify-center">
        <div className="text-gray-500 italic">No email data available</div>
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={`h-full overflow-auto bg-white email_preview ${className}`}
    >
      {/* Sticky Header */}
      {showMetadata &&
        (email?.subject || email?.date || from || to?.length) && (
          <div className="sticky top-0 z-10 bg-white border-b border-gray-200 shadow-sm">
            <div className="p-4">
              {/* Subject - Always at the top */}
              {email?.subject && (
                <h1 className="text-xl font-semibold text-gray-900 mb-3 break-words">
                  {email.subject}
                </h1>
              )}

              {/* Forwarded Message Section */}
              {isForwarded && (
                <div className="mb-3 p-2 bg-blue-50 border-l-4 border-blue-400 rounded-r">
                  <div className="text-sm text-blue-800 font-medium">
                    Forwarded Message
                  </div>
                  {forwardedFrom && (
                    <div className="text-xs text-blue-600 mt-1">
                      Originally from: {forwardedFrom}
                    </div>
                  )}
                </div>
              )}

              {/* Email Metadata */}
              <div className="space-y-2 text-sm">
                {from && (
                  <div className="flex">
                    <span className="font-medium text-gray-600 w-16 flex-shrink-0">
                      From:
                    </span>
                    <span className="text-gray-900 break-all">{from}</span>
                  </div>
                )}

                {email?.date && (
                  <div className="flex">
                    <span className="font-medium text-gray-600 w-16 flex-shrink-0">
                      Date:
                    </span>
                    <span className="text-gray-900">
                      {new Date(email.date).toLocaleString()}
                    </span>
                  </div>
                )}

                {to && to.length > 0 && (
                  <div className="flex">
                    <span className="font-medium text-gray-600 w-16 flex-shrink-0">
                      To:
                    </span>
                    <span className="text-gray-900 break-all">
                      {to.join(", ")}
                    </span>
                  </div>
                )}

                {cc && cc.length > 0 && (
                  <div className="flex">
                    <span className="font-medium text-gray-600 w-16 flex-shrink-0">
                      Cc:
                    </span>
                    <span className="text-gray-900 break-all">
                      {cc.join(", ")}
                    </span>
                  </div>
                )}

                {bcc && bcc.length > 0 && (
                  <div className="flex">
                    <span className="font-medium text-gray-600 w-16 flex-shrink-0">
                      Bcc:
                    </span>
                    <span className="text-gray-900 break-all">
                      {bcc.join(", ")}
                    </span>
                  </div>
                )}

                {email?.subject && (
                  <div className="flex">
                    <span className="font-medium text-gray-600 w-16 flex-shrink-0">
                      Subject:
                    </span>
                    <span className="text-gray-900 break-words">
                      {email.subject}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

      {/* Email Content */}
      <div className="email-content max-w-full overflow-hidden">
        {renderContent()}
      </div>
    </div>
  );
  }
);

EmailViewer.displayName = "EmailViewer";

export default EmailViewer;
